<template>
  <div class="artist-detail-wrapper">
    <div class="detail-container" v-if="artist">
      <!-- 모바일 전용 헤더 -->
      <div class="detail-header mobile-only">
        <button class="back-arrow-btn" @click="$router.push('/artist')" title="목록으로">
          ←
        </button>
        <button class="share-btn" @click="showShareModal = true" title="공유하기">
          <img src="@/assets/images/ios_share.png" alt="공유" class="share-icon" />
        </button>
      </div>

      <!-- <span v-if="isMyArtist" class="my-artist-badge">
        👑 내 아티스트 상세보기입니다
      </span> -->
      <div class="detail-content">
        <div>
          <div class="detail-poster-container">
            <div class="poster-blur-background" :style="posterBackgroundStyle"></div>
            <img v-if="artist.profileImage" :src="artist.profileImage" :alt="artist.name" class="detail-poster" />
            <div v-else class="no-image-placeholder">
              <img :src="typeIconUrl(artist.type)" :alt="artist.type" class="type-icon-large" />
            </div>

            <button class="likeBtnOverlay" style="font-size: 13px; line-height: 1" @click.stop="handleLikeClick()">
              <img v-if="liked" class="like-heart liked" src="@/assets/images/heart.png" />
              <img v-else class="like-heart" src="@/assets/images/heart_lightgray.png" />
            </button>
          </div>
        </div>
        <div class="detail-info">
          <div class="detail-title">
            <div class="title-left">
              <h1>{{ artist.name }}</h1>
              <a v-if="artist.detailLink" class="detail-more-link" @click="goToDetailLink">자세히보기</a>
            </div>
            <div v-if="canManageArtist" class="manage-btn-wrapper" @focusout="onManageWrapperFocusout">
              <button class="manage-settings-btn" @click="showManageDropdown = !showManageDropdown" title="관리 메뉴">
                <img src="@/assets/images/settings.png" alt="관리" class="settings-icon" />
              </button>
              <div v-if="showManageDropdown" class="manage-dropdown">
                <button class="manage-dropdown-item" @click="editArtist(); showManageDropdown = false">
                  수정하기
                </button>
                <button class="manage-dropdown-item manage-dropdown-item--danger"
                  @click="handleDeleteArtist(); showManageDropdown = false">
                  삭제
                </button>
              </div>
            </div>
          </div>

          <div class="info-table">
            <div class="info-row" v-if="artist.type">
              <div class="info-label">타입</div>
              <div class="info-value type-with-icon">
                <!-- <img :src="typeIconUrl(artist.type)" :alt="artist.type" class="type-icon" /> -->
                <span>{{ getTypeDisplayName(artist.type) }}</span>
              </div>
            </div>
            <div class="info-row" v-if="artist.description">
              <div class="info-label">소개글</div>
              <div class="info-value artist-description">
                {{ artist.description }}
              </div>
            </div>
            <div class="info-row" v-if="artist.liveCount > 0">
              <div class="info-label">공연횟수</div>
              <div class="info-value">{{ artist.liveCount }}회</div>
            </div>
            <div class="info-row" v-if="videoCount > 0 && (userStore.auth === 'admin' || userStore.auth === 'beta')">
              <div class="info-label">작품활동</div>
              <div class="info-value">{{ videoCount }}개</div>
            </div>

            <div class="info-row" v-if="hasSnsLinks">
              <div class="info-label">SNS</div>
              <div class="info-value sns-links">
                <a v-for="(sns, index) in snsLinks" :key="index" :href="sns.url" target="_blank"
                  rel="noopener noreferrer" :class="['sns-link', getSnsLinkClass(sns.type)]">
                  <img v-if="getSnsIcon(sns.type)" :src="getSnsIcon(sns.type)" :alt="sns.type" class="sns-icon" />
                  <span>{{ getSnsLabel(sns.type) }}</span>
                </a>
              </div>
            </div>
          </div>

          <!-- 관리자 전용 섹션 -->
          <div v-if="userStore.auth === 'admin'" class="admin-section">
            <div class="admin-section-header" @click="adminSectionOpen = !adminSectionOpen">
              <span class="admin-section-title">🔧 관리자 메뉴</span>
              <span class="admin-section-toggle">{{ adminSectionOpen ? '▲' : '▼' }}</span>
            </div>
            <div v-show="adminSectionOpen" class="admin-section-content">
              <div class="admin-info-table">
                <!-- 사용자 이메일 -->
                <div class="admin-info-row" v-if="artist.userEmail">
                  <div class="admin-info-label">사용자 이메일</div>
                  <div class="admin-info-value">{{ artist.userEmail }}</div>
                </div>
                <!-- 작성자 정보 -->
                <div class="admin-info-row" v-if="artist.createdBy || artist.regId">
                  <div class="admin-info-label">작성자</div>
                  <div class="admin-info-value">
                    <span>{{ artist.createdByNickname || artist.regNickname || "알 수 없음" }}</span>
                    <span class="admin-sub-text">({{ artist.createdBy || artist.regId }})</span>
                    <span v-if="artist.createdAt" class="admin-date-text">{{ formatDateTime(artist.createdAt) }}</span>
                  </div>
                </div>
                <!-- 최종 수정자 정보 -->
                <div class="admin-info-row" v-if="artist.lastModifiedBy">
                  <div class="admin-info-label">최종 수정</div>
                  <div class="admin-info-value">
                    <span>{{ artist.lastModifiedByNickname || "알 수 없음" }}</span>
                    <span class="admin-sub-text">({{ artist.lastModifiedBy }})</span>
                    <span v-if="artist.lastModifiedAt" class="admin-date-text">{{ formatDateTime(artist.lastModifiedAt)
                      }}</span>
                  </div>
                </div>
                <!-- 찜 정보 -->
                <div class="admin-info-row">
                  <div class="admin-info-label">찜 정보</div>
                  <div class="admin-info-value">
                    <span class="like-count-badge">❤️ {{ likeCount }}명</span>
                    <button v-if="likeCount > 0 && !likedUsersLoaded" class="show-liked-btn" @click="fetchLikedUsers"
                      :disabled="likedUsersLoading">
                      {{ likedUsersLoading ? "로딩중..." : "목록 보기" }}
                    </button>
                  </div>
                </div>
                <!-- 찜한 사용자 목록 -->
                <div v-if="likedUsersLoaded && likedUsers.length > 0" class="admin-info-row liked-users-row">
                  <div class="admin-info-label">찜한 사람</div>
                  <div class="admin-info-value liked-users-list">
                    <div v-for="user in likedUsers" :key="user.email" class="liked-user-item">
                      <img v-if="user.profileImage" :src="user.profileImage" :alt="user.nickname"
                        class="liked-user-avatar" />
                      <span v-else class="liked-user-avatar-placeholder">👤</span>
                      <span class="liked-user-name">{{ user.nickname }}</span>
                      <span class="liked-user-email">({{ user.email }})</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 수정 권한 신청 배너 (로그인했고, 수정 권한이 없으며, 본인 아티스트 카드가 없고, 현재 아티스트 카드에 userEmail이 등록되지 않은 경우) -->
      <div v-if="
        userStore.isLogin &&
        !canManageArtist &&
        !hasMyArtistCard &&
        !artist.userEmail
      " class="claim-banner" @click="requestEditPermission">
        <div class="claim-banner-glow"></div>
        <div class="claim-banner-content">
          <div class="claim-icon-circle">
            <div class="claim-icon">✉️</div>
          </div>
          <div class="claim-text">
            <h3 class="claim-title">내 아티스트 카드인가요?</h3>
            <p class="claim-subtitle">
              본인의 아티스트 카드라면 수정 권한을 신청해보세요!
            </p>
            <div class="claim-badge">👑 승인 후 직접 수정 가능</div>
          </div>
        </div>
      </div>

      <!-- 광고 지면 (하단) -->
      <AdSection :adBanners="adBanners" @ad-click="goToAdLink" />

      <!-- 탭 섹션 -->
      <div class="tabs-section">
        <div class="tab-navigation">
          <button :class="['tab-button', { active: activeTab === 'performances' }]" @click="activeTab = 'performances'">
            공연목록
          </button>

          <button v-if="
            this.isArtistBand(artist.type) &&
            (
              (artist.members && artist.members.length > 0) ||
              canManageArtist
            )
          " :class="['tab-button', { active: activeTab === 'members' }]" @click="activeTab = 'members'">
            밴드 멤버
          </button>
          <button v-if="
            !this.isArtistBand(artist.type) &&
            (
              (affiliatedBands && affiliatedBands.length > 0) ||
              isMyArtist
            )
          " :class="['tab-button', { active: activeTab === 'affiliatedBands' }]"
            @click="activeTab = 'affiliatedBands'">
            소속밴드
          </button>
          <button :class="['tab-button', { active: activeTab === 'videos' }]" @click="switchToVideosTab">
            작품활동
          </button>
          <!-- <button :class="['tab-button', { active: activeTab === 'info' }]" @click="activeTab = 'info'">
            아티스트 정보
          </button> -->
        </div>

        <div class="tab-content">
          <!-- 공연목록 탭 -->
          <div v-if="activeTab === 'performances'" class="tab-panel">
            <div v-if="performancesLoading" class="empty-content">
              <div class="spinner"></div>
              <p>공연 정보를 불러오는 중...</p>
            </div>
            <div v-else-if="performances && performances.length > 0" class="performances-grid">
              <PerformanceCard v-for="performance in performances" :key="performance.id" :performance="performance"
                :show-like-button="true" :is-liked="isPerformanceLiked(performance.id)" @click="goToPerformance"
                @like-toggle="togglePerformanceLike" />
            </div>
            <div v-else class="empty-content">등록된 공연이 없습니다.</div>
          </div>

          <!-- 소속 밴드 탭 -->
          <div v-if="activeTab === 'affiliatedBands'" class="tab-panel">
            <div class="bands-content">
              <div v-if="affiliatedBandsForCard && affiliatedBandsForCard.length > 0" class="bands-grid">
                <ArtistCard v-for="(band, index) in affiliatedBandsForCard" :key="index" :artist="band"
                  :is-liked="isArtistLiked(band.id)" :show-like-button="true" :disabled="false" :is-my-card="false"
                  @like-toggle="toggleArtistLike" />
              </div>
              <div v-else class="empty-content">등록된 소속 밴드가 없습니다.</div>
              <div v-if="isMyArtist" class="performance-recommend-banner affiliated-band-edit-banner"
                @click="editArtist">
                <div class="claim-banner-glow"></div>
                <div class="claim-banner-content">
                  <div class="claim-icon-circle">
                    <div class="claim-icon">👥</div>
                  </div>
                  <div class="claim-text">
                    <h3 class="claim-title">소속 밴드 정보가 다른가요?</h3>
                    <p class="claim-subtitle">
                      소속 밴드 정보는 상단 수정하기에서 변경할 수 있어요.
                    </p>
                    <div class="claim-badge">✏️ 수정하기에서 소속 밴드 변경</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 아티스트 정보 탭 -->
          <div v-if="activeTab === 'info'" class="tab-panel">
            <div class="artist-info-content">
              <div class="info-item" v-if="artist.name">
                <h3 class="info-item-label">아티스트명</h3>
                <p class="info-item-value">{{ artist.name }}</p>
              </div>
              <div class="info-item" v-if="artist.type">
                <h3 class="info-item-label">타입</h3>
                <p class="info-item-value">
                  {{ getTypeDisplayName(artist.type) }}
                </p>
              </div>
              <div class="info-item" v-if="artist.liveCount">
                <h3 class="info-item-label">총 공연횟수</h3>
                <p class="info-item-value">{{ artist.liveCount }}회</p>
              </div>
              <div class="info-item" v-if="artist.updatedAt">
                <h3 class="info-item-label">추가 정보</h3>
                <p class="info-item-value">
                  {{ formatDate(artist.updatedAt) }}
                </p>
              </div>
              <div class="info-item" v-if="!artist.type && !artist.liveCount && !artist.updatedAt">
                <div class="empty-content">등록된 상세 정보가 없습니다.</div>
              </div>
            </div>
          </div>

          <!-- 밴드 멤버 탭 -->
          <div v-if="activeTab === 'members'" class="tab-panel">
            <div class="members-content">
              <div v-if="bandMembersForCard && bandMembersForCard.length > 0" class="members-grid">
                <ArtistCard v-for="(member, index) in bandMembersForCard" :key="index" :artist="member"
                  :is-liked="isArtistLiked(member.id)" :show-like-button="true" :disabled="false" :is-my-card="false"
                  @like-toggle="toggleArtistLike" />
              </div>
              <div v-else class="empty-content">등록된 밴드 멤버가 없습니다.</div>

              <div v-if="canManageArtist" class="performance-recommend-banner band-member-edit-banner"
                @click="editArtist">
                <div class="claim-banner-glow"></div>
                <div class="claim-banner-content">
                  <div class="claim-icon-circle">
                    <div class="claim-icon">👥</div>
                  </div>
                  <div class="claim-text">
                    <h3 class="claim-title">밴드 멤버를 추가하고 싶나요?</h3>
                    <p class="claim-subtitle">
                      밴드 멤버 정보는 상단 수정하기에서 추가·변경할 수 있어요.
                    </p>
                    <div class="claim-badge">✏️ 수정하기에서 밴드 멤버 관리</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 작품활동 탭 -->
          <div v-if="activeTab === 'videos'" class="tab-panel">
            <div v-if="videosLoading" class="empty-content">
              <div class="spinner"></div>
              <p>작품활동 정보를 불러오는 중...</p>
            </div>
            <div v-else-if="videos && videos.length > 0" class="videos-grid">
              <ArtistVideoCard v-for="video in videos" :key="video.id" :video="video" :show-like-button="true"
                :is-liked="isVideoLiked(video.id)" @like-toggle="toggleVideoLike" />
            </div>
            <div v-else class="empty-content">등록된 작품활동이 없습니다.</div>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="loading">
      <div class="spinner"></div>
      <p>아티스트 정보를 불러오는 중...</p>
    </div>

    <!-- 수정 팝업 -->
    <artist-form :is-visible="showUpdatePopup" mode="update" :artist-data="artistForEdit"
      @close-popup="closeUpdatePopup" @artist-updated="handleArtistUpdated"></artist-form>

    <!-- 작품 등록 플로팅 버튼 (작품활동 탭에서만 표시) -->
    <button v-if="activeTab === 'videos'" @click="openVideoPopup" class="floating-button">
      <span>+ 작품등록</span>
    </button>

    <!-- 작품(비디오) 등록 팝업 -->
    <video-form :is-visible="showVideoPopup" :edit-mode="false" :default-artist="artist?.name || ''"
      :default-artist-image="artist?.profileImage || ''" @close-popup="closeVideoPopup"
      @upload-success="onVideoUploadSuccess"></video-form>

    <!-- 공유 방법 선택 모달 -->
    <share-options-modal :visible="showShareModal" @close="showShareModal = false"
      @select="handleShareMethod"></share-options-modal>
  </div>
</template>

<script>
import {
  collection,
  getDocs,
  doc,
  getDoc,
  query,
  where,
} from "firebase/firestore";
import { ref as storageRef } from "firebase/storage";
import { getDownloadURL } from "@/utils/storageWithNavigation";
import { firestore, storage } from "/firebaseConfig";
import { useUserStore } from "@/store/user";
import ArtistForm from "@/components/modal/form/ArtistForm.vue";
import AdSection from "@/components/AdSection.vue";
import scrollManager from "@/utils/scrollManager";
import { deleteArtist as deleteArtistApi } from "@/utils/write/artist";
import { getArtistTypeDisplayName, getArtistTypeIconKey } from "@/utils/artistTypes";
import {
  trackArtistDetailView,
  trackArtistLinkClick,
} from "@/utils/statistics";
import { toggleArtistLike, toggleLiveLike } from "@/utils/write/user";
import {
  getUserLikes,
  getUsersByArtistLike,
  getArtistLikeCount,
  getUserDetailLink,
} from "@/utils/read/user";
import { createNotification } from "@/utils/write/alarm";
import { incrementBannerClickCount } from "@/utils/write/banner";
import { parse, isValid } from "date-fns";
import PerformanceCard from "@/components/PerformanceCard.vue";
import { trackActivity, ACTIVITY_TYPES, trackShare } from "@/utils/activityTracker";
import ArtistCard from "@/components/ArtistCard.vue";
import VideoForm from "@/components/video/VideoForm.vue";
import ArtistVideoCard from "@/components/ArtistVideoCard.vue";
import { setPageMeta, shareViaKakao, shareViaTwitter } from "@/utils/pageMeta";
import ShareOptionsModal from "@/components/modal/ShareOptionsModal.vue";

export default {
  name: "ArtistDetail",
  components: {
    ArtistForm,
    AdSection,
    PerformanceCard,
    ArtistCard,
    VideoForm,
    ArtistVideoCard,
    ShareOptionsModal,
  },
  data() {
    return {
      artist: null,
      performances: [],
      loading: true,
      performancesLoading: false, // 공연 목록 로딩 상태
      error: null,
      userStore: useUserStore(),
      liked: false,
      userArtistLikes: [],
      userLiveLikes: [], // 사용자가 찜한 공연 목록
      activeTab: "performances",
      showUpdatePopup: false,
      artistForEdit: null,
      membersInfo: [], // 밴드 멤버들의 상세 정보
      affiliatedBands: [], // 소속 밴드들의 상세 정보
      userDetailLink: null, // 현재 사용자의 트위터 링크
      hasMyArtistCard: false, // 사용자가 아티스트 카드를 가지고 있는지 여부
      adBanners: [], // 광고 배너 목록
      adminSectionOpen: false, // 관리자 섹션 열림 상태
      // 찜한 사용자 관련 (관리자용)
      likeCount: 0,
      likedUsers: [],
      likedUsersLoading: false,
      likedUsersLoaded: false,
      // 작품활동(비디오) 관련
      videos: [],
      videosLoading: false,
      showVideoPopup: false, // 비디오 등록 팝업
      userVideoLikes: [], // 사용자가 찜한 비디오 목록
      videoCount: 0, // 작품활동 개수
      lastPerformanceArtistName: null, // 현재 성능 캐시와 연결된 아티스트명
      livePosterUrlCache: {}, // lives 포스터 URL 캐시
      performanceImageLoadToken: 0, // 포스터 비동기 로드 충돌 방지 토큰
      showShareModal: false,
      showManageDropdown: false,
    };
  },
  computed: {
    posterBackgroundStyle() {
      return this.artist?.profileImage
        ? `background-image: url(${this.artist.profileImage})`
        : "";
    },
    // SNS 링크 목록 (기존 필드 호환 + 새로운 snsList 형식)
    snsLinks() {
      if (!this.artist) return [];
      if (this.artist.snsList && Array.isArray(this.artist.snsList)) {
        return this.artist.snsList.filter((s) => s.type && s.url);
      }
      // 기존 youtubeChannel, instagram 필드 호환
      const links = [];
      if (this.artist.youtubeChannel) {
        links.push({ type: "youtube", url: this.artist.youtubeChannel });
      }
      if (this.artist.instagram) {
        links.push({ type: "instagram", url: this.artist.instagram });
      }
      return links;
    },
    hasSnsLinks() {
      return this.snsLinks.length > 0;
    },
    // 아티스트 관리(수정/삭제) 권한 체크 (통합)
    canManageArtist() {
      if (!this.userStore.isLogin || !this.artist) return false;

      // 관리자는 항상 가능
      if (this.userStore.auth === "admin") return true;

      // 아티스트에 등록된 사용자 이메일과 로그인 사용자가 동일하면 가능
      if (this.artist?.userEmail && this.artist.userEmail === this.userStore.email) {
        return true;
      }

      // 작성자(레거시/호환 필드)와 로그인 사용자가 동일하면 가능
      if (this.artist.createdBy && this.artist.createdBy === this.userStore.email) {
        return true;
      }

      // 본인이 트위터 연동을 했고, 아티스트의 detailLink와 일치하는 경우 가능
      if (this.userDetailLink && this.artist?.detailLink && this.userDetailLink === this.artist.detailLink) {
        return true;
      }

      // 밴드인 경우, 본인이 해당 밴드의 멤버인지 확인
      if (
        this.userDetailLink &&
        this.artist?.type &&
        this.isArtistBand(this.artist.type) &&
        this.artist.members &&
        this.artist.members.length > 0
      ) {
        const isBandMember = this.membersInfo.some(
          (member) => member.detailLink && member.detailLink === this.userDetailLink
        );
        if (isBandMember) return true;
      }

      return false;
    },
    // 기존 템플릿/로직 호환용 (deprecated)
    canEditArtist() {
      return this.canManageArtist;
    },
    // 기존 템플릿/로직 호환용 (deprecated)
    canDelete() {
      return this.canManageArtist;
    },
    // 내 아티스트인지 확인
    isMyArtist() {
      if (!this.userStore.isLogin || !this.userDetailLink || !this.artist) {
        return false;
      }

      // 본인의 트위터 링크와 아티스트의 detailLink가 일치하는 경우
      if (
        this.artist.detailLink &&
        this.userDetailLink === this.artist.detailLink
      ) {
        return true;
      }

      // 밴드의 멤버인 경우
      if (
        this.artist.type &&
        this.isArtistBand(this.artist.type) &&
        this.artist.members &&
        this.artist.members.length > 0
      ) {
        const isBandMember = this.membersInfo.some(
          (member) =>
            member.detailLink && member.detailLink === this.userDetailLink
        );
        return isBandMember;
      }

      return false;
    },
    // 밴드 멤버를 ArtistCard 형식으로 변환
    bandMembersForCard() {
      if (!this.artist || !this.artist.members) return [];

      // members는 이제 문서 ID 배열
      return this.artist.members.map(memberId => {
        // ID로 멤버 정보 찾기
        const memberInfo = this.membersInfo.find(m => m.id === memberId);
        if (memberInfo) {
          return {
            id: memberInfo.id,
            name: memberInfo.name,
            type: memberInfo.type || 'other',
            profileImage: memberInfo.profileImage || null,
          };
        } else {
          // membersInfo에 없는 경우 (조회 실패 등)
          return {
            id: memberId,
            name: '알 수 없음',
            type: 'other',
            profileImage: null,
          };
        }
      });
    },
    // 소속 밴드를 ArtistCard 형식으로 변환
    affiliatedBandsForCard() {
      if (!this.affiliatedBands) return [];

      return this.affiliatedBands.map(band => ({
        id: band.id || null,
        slug: band.slug ?? null,
        name: band.name,
        type: band.type || 'band',
        profileImage: band.profileImage || null,
      }));
    },
  },
  watch: {
    // 라우트 파라미터 변경 감지
    async $route(to, from) {
      // 같은 ArtistDetail 컴포넌트 내에서 다른 아티스트로 이동할 때
      if (to.name === from.name && to.params.slug !== from.params.slug) {
        await this.loadArtistData(to.params.slug);
      }
    },
  },
  async created() {
    const artistSlug = this.$route.params.slug;
    await Promise.all([
      this.loadArtistData(artistSlug),
      this.loadUserDetailLink(), // 사용자 트위터 링크 로드
      this.fetchAdBanners(), // 광고 배너 로드
    ]);
  },
  methods: {
    async handleShareMethod(method) {
      this.showShareModal = false;
      const url = window.location.href;
      const title = this.artist?.name || '아티스트';

      if (method === 'kakao') {
        await shareViaKakao();
        trackShare("artist", this.artist?.id, "kakao");
        return;
      }
      if (method === 'twitter') {
        shareViaTwitter();
        trackShare("artist", this.artist?.id, "twitter");
        return;
      }
      if (method === 'copy') {
        trackShare("artist", this.artist?.id, "copy");
        this.copyToClipboard(url);
        return;
      }
      if (method === 'native' && navigator.share) {
        try {
          await navigator.share({ title, text: `${title} - 링꾹라이브`, url });
          trackShare("artist", this.artist?.id, "native");
        } catch (error) {
          if (error.name !== 'AbortError') {
            trackShare("artist", this.artist?.id, "copy_fallback");
            this.copyToClipboard(url);
          }
        }
      }
    },

    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$alert('링크가 복사되었습니다.');
      }).catch(() => {
        // fallback
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        this.$alert('링크가 복사되었습니다.');
      });
    },

    // 아티스트 데이터 로드 (라우트 변경 시 재사용)
    async loadArtistData(artistSlug) {
      // 기존 데이터 초기화
      this.artist = null;
      this.performances = [];
      this.performancesLoading = true;
      this.lastPerformanceArtistName = null;
      this.membersInfo = [];
      this.affiliatedBands = [];
      this.activeTab = "performances";

      // 아티스트 정보 먼저 로드
      await this.fetchArtist(artistSlug);

      // 아티스트 로드 후 찜 상태 로드 (아티스트 ID 기반)
      if (this.artist) {
        await this.loadUserArtistLikes(this.artist.id);
      }

      // 밴드인 경우 멤버 정보 로드
      if (
        this.artist &&
        this.isArtistBand(this.artist.type) &&
        this.artist.members
      ) {
        await this.fetchMembersInfo();
      }

      // 밴드가 아닌 경우 소속 밴드 정보 로드
      if (this.artist && !this.isArtistBand(this.artist.type)) {
        await this.fetchAffiliatedBands();
      }

      // 공연 횟수 및 공연 목록 재계산 (소속 밴드 정보 로드 후)
      if (this.artist) {
        // artist.name을 사용하여 공연 조회 (공연은 아티스트명으로 연결됨)
        await this.fetchPerformances(this.artist.name);
        this.artist.liveCount = this.performances.length;

        // 관리자인 경우 찜 수 가져오기
        if (this.userStore.auth === "admin") {
          await this.fetchLikeCount();
        }
      } else {
        this.performancesLoading = false;
      }
    },

    async fetchArtist(artistSlug) {
      try {
        this.loading = true;
        this.error = null;

        const normalizedSlug = Number(artistSlug);
        let artistDocSnap = null;

        if (Number.isFinite(normalizedSlug)) {
          const artistSlugQuery = query(
            collection(firestore, "artists"),
            where("slug", "==", normalizedSlug)
          );
          const artistSlugSnapshot = await getDocs(artistSlugQuery);
          if (!artistSlugSnapshot.empty) {
            artistDocSnap = artistSlugSnapshot.docs[0];
          }
        }

        // 과거 공유 링크(/artist/{docId}) 호환을 위한 fallback
        if (!artistDocSnap) {
          const artistDocRef = doc(firestore, "artists", String(artistSlug));
          const fallbackDocSnap = await getDoc(artistDocRef);
          if (fallbackDocSnap.exists()) {
            artistDocSnap = fallbackDocSnap;
          }
        }

        if (!artistDocSnap || !artistDocSnap.exists()) {
          this.error = "아티스트 정보를 찾을 수 없습니다.";
          return;
        }

        const artistData = artistDocSnap.data();

        // 승인되지 않은 아티스트 체크
        if (!artistData.approved) {
          this.error = "아티스트 정보를 찾을 수 없습니다.";
          return;
        }

        const artistDoc = artistDocSnap;

        // 프로필 이미지 URL 생성
        let profileImageUrl = null;
        if (artistData.userProfileImg) {
          try {
            const imageRef = storageRef(
              storage,
              `profile/${artistData.userProfileImg}`
            );
            profileImageUrl = await getDownloadURL(imageRef);
          } catch (error) {
            console.warn("사용자 프로필 이미지 로드 실패:", error);
          }
        } else if (artistData.img) {
          try {
            const imageRef = storageRef(storage, `artist/${artistData.img}`);
            profileImageUrl = await getDownloadURL(imageRef);
          } catch (error) {
            console.warn("아티스트 이미지 로드 실패:", error);
          }
        }

        this.artist = {
          id: artistDoc.id, // 문서 ID 추가
          slug: artistData.slug ?? null,
          name: artistData.name,
          type: artistData.type || null,
          category: artistData.category || null, // 아티스트 분류 (band/individual)
          detailLink: artistData.detailLink || null,
          profileImage: profileImageUrl,
          img: artistData.img || null, // 실제 저장된 파일명
          description: artistData.description || null, // 소개 정보
          updatedAt: artistData.updatedAt || null,
          liveCount: 0, // 초기값으로 설정, 나중에 계산
          members: artistData.members || null, // 밴드 멤버 정보 추가
          myBands: artistData.myBands || artistData.MyBands || null, // 소속 밴드 정보 (대소문자 모두 지원)
          userEmail: artistData.userEmail || null, // 사용자 이메일
          // SNS 정보
          snsList: artistData.snsList || null,
          youtubeChannel: artistData.youtubeChannel || null, // 기존 필드 호환
          instagram: artistData.instagram || null, // 기존 필드 호환
          // 작성자 정보
          createdBy: artistData.createdBy || null,
          createdByNickname: artistData.createdByNickname || null,
          createdAt: artistData.createdAt || null,
          regId: artistData.regId || null,
          regNickname: artistData.regNickname || null,
          // 최종 수정자 정보
          lastModifiedBy: artistData.lastModifiedBy || null,
          lastModifiedByNickname: artistData.lastModifiedByNickname || null,
          lastModifiedAt: artistData.lastModifiedAt || null,
        };

        setPageMeta({
          title: this.artist.name,
          description: this.artist.description || undefined,
          image: this.artist.profileImage || undefined,
        });

        // 아티스트 상세 화면 접속 통계 추적
        trackArtistDetailView(artistDoc.id);

        // 작품활동 개수 카운트 (userEmail이 있는 경우만)
        if (this.artist.userEmail) {
          await this.fetchVideoCount();
        }
      } catch (error) {
        console.error("아티스트 정보 조회 오류:", error);
        this.error = "아티스트 정보를 불러오는 중 오류가 발생했습니다.";
      } finally {
        this.loading = false;
      }
    },

    // 작품활동 개수 가져오기
    async fetchVideoCount() {
      if (!this.artist?.userEmail) {
        this.videoCount = 0;
        return;
      }

      try {
        const videosRef = collection(firestore, "videos");
        const q = query(
          videosRef,
          where("registeredBy", "==", this.artist.userEmail)
        );
        const querySnapshot = await getDocs(q);
        this.videoCount = querySnapshot.docs.filter((doc) => {
          const data = doc.data();
          return !data.liveId;
        }).length;
      } catch (error) {
        console.error("작품활동 개수 로드 오류:", error);
        this.videoCount = 0;
      }
    },

    async fetchPerformances(artistName) {
      const loadToken = ++this.performanceImageLoadToken;
      this.performancesLoading = true;
      try {
        const livesRef = collection(firestore, "lives");
        const approvedLivesQuery = query(livesRef, where("approved", "==", true));
        const livesSnapshot = await getDocs(approvedLivesQuery);
        const matchedLives = [];
        const isBandArtist = this.artist && this.isArtistBand(this.artist.type);

        // 소속 밴드명 목록 (현재 아티스트가 밴드가 아닌 경우)
        const affiliatedBandNames = this.affiliatedBands
          ? this.affiliatedBands.map((band) => band.name)
          : [];

        for (const doc of livesSnapshot.docs) {
          const liveData = doc.data();

          const lineup = liveData.lineup || [];
          const crew = Array.isArray(liveData.crew) ? liveData.crew : [];

          let isInLineup = false;
          let isInCrew = false;
          let crewRole = "";
          let participationType = isBandArtist ? "" : "solo"; // 'solo' | 'band' | 'crew'

          // 개별 아티스트로 참여한 공연 확인
          if (Array.isArray(lineup)) {
            isInLineup = lineup.includes(artistName);
          } else if (typeof lineup === "string") {
            isInLineup = lineup === artistName;
          }

          // 소속 밴드로 참여한 공연 확인 (밴드가 아닌 경우에만)
          if (
            !isInLineup &&
            this.artist &&
            !isBandArtist &&
            affiliatedBandNames.length > 0
          ) {
            if (Array.isArray(lineup)) {
              const matchedBand = lineup.find((name) =>
                affiliatedBandNames.includes(name)
              );
              if (matchedBand) {
                isInLineup = true;
                participationType = "band";
              }
            } else if (typeof lineup === "string") {
              if (affiliatedBandNames.includes(lineup)) {
                isInLineup = true;
                participationType = "band";
              }
            }
          }

          // 함께한 사람(crew)으로 참여한 공연 확인
          if (!isBandArtist && Array.isArray(crew) && crew.length > 0) {
            const matchedCrew = crew.find((member) => {
              if (typeof member === "object" && member !== null) {
                return (
                  String(member.name || "").trim() === artistName &&
                  String(member.role || "").trim()
                );
              }
              return false;
            });

            if (matchedCrew) {
              isInCrew = true;
              if (typeof matchedCrew === "object" && matchedCrew !== null) {
                crewRole = String(matchedCrew.role || "").trim();
              }
            }
          }

          if (isInCrew && !isInLineup) {
            participationType = "crew";
          }

          if (isInLineup || isInCrew) {
            matchedLives.push({
              id: doc.id,
              title: liveData.title,
              date: liveData.date,
              place: liveData.place,
              rawImg: liveData.img || null, // 포스터 비동기 로딩용 원본 값
              img:
                typeof liveData.img === "string" && liveData.img.startsWith("http")
                  ? liveData.img
                  : null,
              participationType: participationType, // 참여 형태 정보 추가
              isLineupParticipation: isBandArtist ? false : isInLineup,
              isCrewParticipation: isInCrew,
              crewRole: crewRole,
            });
          }
        }

        if (loadToken !== this.performanceImageLoadToken) {
          return;
        }

        this.performances = matchedLives;
        this.lastPerformanceArtistName = artistName;
        this.performancesLoading = false;
        this.schedulePerformancePostProcess(loadToken);
      } catch (error) {
        console.error("공연 목록 조회 오류:", error);
        if (loadToken === this.performanceImageLoadToken) {
          this.performancesLoading = false;
        }
      }
    },

    schedulePerformancePostProcess(loadToken) {
      this.$nextTick(() => {
        if (loadToken !== this.performanceImageLoadToken) {
          return;
        }

        const runLoad = () => {
          if (loadToken !== this.performanceImageLoadToken) {
            return;
          }
          this.sortPerformancesByDate();
          this.loadPerformancePosterImages(loadToken);
        };

        if (typeof window !== "undefined" && "requestIdleCallback" in window) {
          window.requestIdleCallback(runLoad, { timeout: 1000 });
          return;
        }

        setTimeout(runLoad, 0);
      });
    },

    sortPerformancesByDate() {
      this.performances.sort((a, b) => {
        // 날짜가 없는 경우 처리
        if (!a.date && !b.date) return 0;
        if (!a.date) return 1; // a가 없으면 뒤로
        if (!b.date) return -1; // b가 없으면 뒤로

        // LinkookLiveTiles와 동일한 날짜 파싱 방식
        const parseDate = (dateStr) => {
          const tempDateStr = dateStr.replace(/\./g, "-").trim(); // 점(.)을 대시(-)로 변경
          const formattedDateStr = !tempDateStr.includes(":") // 시간이 없는 경우 기본값 " 00:00" 추가
            ? tempDateStr + " 00:00"
            : tempDateStr;
          return parse(formattedDateStr, "yyyy-MM-dd HH:mm", new Date());
        };

        const dateA = parseDate(a.date);
        const dateB = parseDate(b.date);

        // 날짜가 유효한지 확인 (Invalid Date 방지)
        if (!isValid(dateA) || !isValid(dateB)) {
          console.error("Invalid date detected:", a.date, b.date);
          if (!isValid(dateA) && !isValid(dateB)) return 0;
          if (!isValid(dateA)) return 1;
          return -1;
        }

        // 날짜 부분만 비교 (시간 제외) - LinkookLiveTiles 방식
        const getDateOnly = (date) => {
          return new Date(date.getFullYear(), date.getMonth(), date.getDate());
        };
        const dateOnlyA = getDateOnly(dateA);
        const dateOnlyB = getDateOnly(dateB);

        // 최신순 정렬 (b.date - a.date)
        return dateOnlyB.getTime() - dateOnlyA.getTime();
      });
    },

    async loadPerformancePosterImages(loadToken) {
      const tasks = this.performances.map(async (performance) => {
        if (!performance.rawImg || performance.img) {
          return;
        }

        const imageUrl = await this.resolveLivePosterUrl(performance.rawImg);
        if (!imageUrl || loadToken !== this.performanceImageLoadToken) {
          return;
        }

        const targetPerformance = this.performances.find(
          (item) => item.id === performance.id
        );
        if (targetPerformance) {
          targetPerformance.img = imageUrl;
        }
      });

      await Promise.allSettled(tasks);
    },

    async resolveLivePosterUrl(rawImg) {
      if (!rawImg) return null;
      if (rawImg.startsWith("http")) return rawImg;

      if (this.livePosterUrlCache[rawImg]) {
        return this.livePosterUrlCache[rawImg];
      }

      try {
        const imageRef = storageRef(storage, `poster/${rawImg}`);
        const url = await getDownloadURL(imageRef);
        this.livePosterUrlCache[rawImg] = url;
        return url;
      } catch (error) {
        console.warn("포스터 이미지 로드 실패:", error);
        return null;
      }
    },

    async loadUserArtistLikes(artistId) {
      if (!this.userStore.isLogin) {
        this.userArtistLikes = [];
        this.userLiveLikes = [];
        this.liked = false;
        return;
      }

      try {
        const userLikes = await getUserLikes(this.userStore.email);
        this.userArtistLikes = userLikes.artistLikes || [];
        this.userLiveLikes = userLikes.liveLikes || [];
        // artistId가 전달되지 않은 경우 this.artist.id 사용
        const targetArtistId = artistId || this.artist?.id;
        this.liked = targetArtistId
          ? this.userArtistLikes.includes(targetArtistId)
          : false;
      } catch (error) {
        console.error("사용자 찜 데이터 로드 오류:", error);
        this.userArtistLikes = [];
        this.userLiveLikes = [];
        this.liked = false;
      }
    },

    // 찜한 사용자 목록 및 수 가져오기 (관리자용)
    async fetchLikedUsers() {
      if (!this.artist?.name || this.likedUsersLoaded) return;

      this.likedUsersLoading = true;
      try {
        const users = await getUsersByArtistLike(this.artist.name);
        this.likedUsers = users;
        this.likeCount = users.length;
        this.likedUsersLoaded = true;
      } catch (error) {
        console.error("찜한 사용자 목록 가져오기 실패:", error);
      } finally {
        this.likedUsersLoading = false;
      }
    },

    // 찜 수만 가져오기 (관리자용, 초기 로드용)
    async fetchLikeCount() {
      if (!this.artist?.name) return;

      try {
        this.likeCount = await getArtistLikeCount(this.artist.name);
      } catch (error) {
        console.error("찜 수 가져오기 실패:", error);
      }
    },

    // 찜 버튼 클릭 핸들러
    handleLikeClick() {
      if (!this.userStore.isLogin) {
        this.userStore.showLoginPopup('artistLike');
        return;
      }
      this.toggleLike();
    },
    async toggleLike() {
      if (!this.artist) return;

      try {
        const newLikedState = await toggleArtistLike(
          this.artist.id,
          this.userStore.email,
          this.liked
        );

        // 로컬 상태 업데이트
        if (newLikedState) {
          // 찜 추가
          this.userArtistLikes.push(this.artist.id);

          // 아티스트에게 알림 전송
          if (this.artist.userEmail && this.artist.userEmail !== this.userStore.email) {
            try {
              await createNotification({
                userId: this.artist.userEmail,
                type: "ARTIST_LIKE",
                title: "새로운 팬이 생겼어요! 💖",
                message: `${this.userStore.nickname || '누군가'}님이 ${this.artist.name}을(를) 찜했습니다.`,
                targetType: "artist",
                targetId: this.artist.id,
                fromUserId: this.userStore.email,
                fromUserNickname: this.userStore.nickname || null,
              });
            } catch (notifError) {
              console.warn("알림 전송 실패:", notifError);
            }
          }
        } else {
          // 찜 해제
          this.userArtistLikes = this.userArtistLikes.filter(
            (id) => id !== this.artist.id
          );
        }

        this.liked = newLikedState;
      } catch (error) {
        console.error("아티스트 찜 오류:", error);
        this.$alert("오류가 발생했습니다. 다시 시도해주세요.");
      }
    },

    goToDetailLink() {
      if (this.artist && this.artist.detailLink) {
        // 아티스트 링크이동 클릭 통계 추적
        trackArtistLinkClick(this.artist.id);

        // 자세히보기 활동 기록
        trackActivity(ACTIVITY_TYPES.DETAIL_LINK_CLICK, {
          target: "artist",
          targetId: this.artist.name,
          metadata: { link: this.artist.detailLink },
        });

        window.open(this.artist.detailLink, "_blank");
      }
    },

    async handleDeleteArtist() {
      const confirmed = await this.$confirm(
        `정말로 "${this.artist.name}" 아티스트를 삭제하시겠습니까?`
      );

      if (!confirmed) return;

      try {
        // Firestore에서 아티스트 삭제 (ID 사용)
        if (!this.artist.id) {
          this.$alert("아티스트 ID를 찾을 수 없습니다.");
          return;
        }
        await deleteArtistApi(this.artist.id);

        await this.$alert("아티스트가 삭제되었습니다.");

        // 아티스트 목록 페이지로 이동
        await this.$nextTick();
        this.$router.push("/artist");
      } catch (error) {
        console.error("아티스트 삭제 오류:", error);
        this.$alert("아티스트 삭제 중 오류가 발생했습니다.");
      }
    },

    goToPerformance(performanceId) {
      this.$router.push(`/live/${performanceId}`);
    },

    typeIconUrl(type) {
      const iconKey = getArtistTypeIconKey(type);
      try {
        return require(`@/assets/images/${iconKey}.png`);
      } catch {
        return require("@/assets/images/mix.png");
      }
    },

    getTypeDisplayName(type) {
      return getArtistTypeDisplayName(type);
    },

    // 배열 구조를 고려한 밴드 타입 체크 헬퍼 메서드
    isArtistBand(type) {
      if (!type) return false;
      if (Array.isArray(type)) {
        return type.includes("band");
      }
      return type === "band";
    },

    // SNS 관련 메서드
    getSnsLinkClass(type) {
      const classMap = {
        youtube: "youtube-link",
        instagram: "instagram-link",
        twitter: "twitter-link",
        tiktok: "tiktok-link",
        soundcloud: "soundcloud-link",
        other: "other-link",
      };
      return classMap[type] || "other-link";
    },

    getSnsIcon(type) {
      const iconMap = {
        youtube: require("@/assets/images/youtube.png"),
        instagram: require("@/assets/images/insta.png"),
        twitter: require("@/assets/images/xlogowhite.png"),
        tiktok: require("@/assets/images/tiktok.png"),
        soundcloud: require("@/assets/images/soundcloud.png"),
      };
      return iconMap[type] || null;
    },

    getSnsLabel(type) {
      const labelMap = {
        youtube: "유튜브",
        instagram: "인스타그램",
        twitter: "X(트위터)",
        tiktok: "틱톡",
        soundcloud: "사운드클라우드",
        other: "기타",
      };
      return labelMap[type] || type;
    },

    formatDate(date) {
      if (!date) return "정보 없음";

      try {
        const dateObj = date instanceof Date ? date : new Date(date);
        if (isNaN(dateObj.getTime())) return "정보 없음";

        return dateObj.toLocaleDateString("ko-KR", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        });
      } catch (error) {
        return "정보 없음";
      }
    },

    formatDateTime(date) {
      if (!date) return "";

      try {
        let dateObj;
        if (date.toDate && typeof date.toDate === "function") {
          // Firestore Timestamp
          dateObj = date.toDate();
        } else if (date instanceof Date) {
          dateObj = date;
        } else {
          dateObj = new Date(date);
        }

        if (isNaN(dateObj.getTime())) return "";

        return dateObj.toLocaleString("ko-KR", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch (error) {
        console.error("날짜 포맷팅 오류:", error);
        return "";
      }
    },

    // 공연 찜 관련 메서드들
    isPerformanceLiked(performanceId) {
      return this.userLiveLikes.includes(performanceId);
    },

    async togglePerformanceLike(performanceId) {
      if (!this.userStore.isLogin) {
        this.userStore.showLoginPopup('liveLike');
        return;
      }

      try {
        const isCurrentlyLiked = this.isPerformanceLiked(performanceId);

        const newLikedState = await toggleLiveLike(
          performanceId,
          this.userStore.email,
          isCurrentlyLiked
        );

        // 로컬 상태 업데이트
        if (newLikedState) {
          // 찜 추가
          this.userLiveLikes.push(performanceId);
        } else {
          // 찜 해제
          this.userLiveLikes = this.userLiveLikes.filter(
            (id) => id !== performanceId
          );
        }
      } catch (error) {
        console.error("공연 찜 오류:", error);
        this.$alert("오류가 발생했습니다. 다시 시도해주세요.");
      }
    },

    // 작품활동 탭으로 전환
    switchToVideosTab() {
      this.activeTab = 'videos';
      if (this.videos.length === 0 && !this.videosLoading) {
        this.fetchArtistVideos();
      }
    },

    // 아티스트 관련 비디오 가져오기
    async fetchArtistVideos() {
      // 아티스트에 연결된 userEmail이 없으면 빈 목록
      if (!this.artist?.userEmail) {
        this.videos = [];
        this.videosLoading = false;
        return;
      }

      this.videosLoading = true;
      try {
        const videosRef = collection(firestore, "videos");
        // 등록자 이메일 기준으로 검색
        const q = query(
          videosRef,
          where("registeredBy", "==", this.artist.userEmail)
        );
        const querySnapshot = await getDocs(q);

        const videos = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.title && !data.liveId) {
            videos.push({
              id: doc.id,
              ...data,
            });
          }
        });

        // 날짜순 정렬 (최신순)
        videos.sort((a, b) => {
          if (!a.date) return 1;
          if (!b.date) return -1;
          return new Date(b.date) - new Date(a.date);
        });

        this.videos = videos;
        // 비디오 찜 상태 로드
        await this.loadUserVideoLikes();
      } catch (error) {
        console.error("작품활동 로드 오류:", error);
        this.videos = [];
      } finally {
        this.videosLoading = false;
      }
    },

    // 사용자 비디오 찜 목록 로드
    async loadUserVideoLikes() {
      if (!this.userStore.isLogin) {
        this.userVideoLikes = [];
        return;
      }

      try {
        const userLikes = await getUserLikes(this.userStore.email);
        this.userVideoLikes = userLikes.videoLikes || [];
      } catch (error) {
        console.error("사용자 비디오 찜 데이터 로드 오류:", error);
        this.userVideoLikes = [];
      }
    },

    // 비디오 찜 여부 확인
    isVideoLiked(videoId) {
      return this.userVideoLikes.includes(videoId);
    },

    // 비디오 찜 토글
    async toggleVideoLike(videoId) {
      if (!this.userStore.isLogin) {
        this.userStore.showLoginPopup('videoLike');
        return;
      }

      try {
        const { toggleVideoLike: toggleVideoLikeApi } = await import("@/utils/write/user");
        const isLiked = await toggleVideoLikeApi(videoId, this.userStore.email);

        const targetVideo = this.videos.find(v => v.id === videoId);
        if (targetVideo) {
          if (isLiked) {
            // 좋아요 추가
            targetVideo.likes = (targetVideo.likes || 0) + 1;
            // 영상 등록자에게 알림 전송 (본인 영상이면 제외)
            const ownerEmail = targetVideo.registeredBy;
            if (ownerEmail && ownerEmail !== this.userStore.email) {
              try {
                const videoTitle = targetVideo.title?.length > 25
                  ? targetVideo.title.substring(0, 25) + "..."
                  : (targetVideo.title || "영상");
                await createNotification({
                  userId: ownerEmail,
                  type: "VIDEO_LIKE",
                  title: "영상에 찜을 받았어요! 💖",
                  message: `${this.userStore.nickname || "누군가"}님이 "${videoTitle}" 영상을 찜했습니다.`,
                  targetType: "video",
                  targetId: videoId,
                  fromUserId: this.userStore.email,
                  fromUserNickname: this.userStore.nickname || null,
                });
              } catch (notifError) {
                console.warn("영상 찜 알림 전송 실패:", notifError);
              }
            }
          } else {
            // 좋아요 취소
            targetVideo.likes = Math.max(0, (targetVideo.likes || 0) - 1);
          }
        }

        // 로컬 상태 업데이트
        if (isLiked) {
          this.userVideoLikes.push(videoId);
        } else {
          this.userVideoLikes = this.userVideoLikes.filter(id => id !== videoId);
        }
      } catch (error) {
        console.error("비디오 찜 오류:", error);
        this.$alert("오류가 발생했습니다. 다시 시도해주세요.");
      }
    },

    // 비디오 등록 팝업 열기
    openVideoPopup() {
      this.showVideoPopup = true;
      scrollManager.lock("artistVideoForm");
    },

    // 비디오 등록 팝업 닫기
    closeVideoPopup() {
      this.showVideoPopup = false;
      scrollManager.unlock("artistVideoForm");
    },

    // 비디오 등록 성공
    onVideoUploadSuccess() {
      this.closeVideoPopup();
      // 비디오 목록 다시 로드
      this.videos = [];
      this.fetchArtistVideos();
      // 작품활동 개수 업데이트
      this.videoCount++;
    },

    onManageWrapperFocusout(e) {
      if (!e.currentTarget.contains(e.relatedTarget)) {
        this.showManageDropdown = false;
      }
    },

    editArtist() {
      // 수정용 아티스트 데이터 구성
      this.artistForEdit = {
        id: this.artist.id, // 문서 ID (필수)
        name: this.artist.name,
        type: this.artist.type,
        category: this.artist.category, // 아티스트 분류 (band/individual)
        detailLink: this.artist.detailLink,
        profileImage: this.artist.profileImage,
        img: this.artist.img, // 실제 저장된 파일명
        members: this.artist.members, // 밴드 멤버 정보
        myBands: this.artist.myBands || this.artist.MyBands || [], // 소속 밴드 정보
        description: this.artist.description, // 소개 정보
        userEmail: this.artist.userEmail, // 사용자 이메일 (관리자 전용)
        // SNS 정보
        snsList: this.artist.snsList,
        youtubeChannel: this.artist.youtubeChannel,
        instagram: this.artist.instagram,
      };
      this.showUpdatePopup = true;
      scrollManager.lock("artistDetailUpdate");
    },

    async closeUpdatePopup() {
      this.showUpdatePopup = false;
      scrollManager.unlock("artistDetailUpdate");

      // 아티스트 정보 다시 불러오기
      const artistSlug = this.$route.params.slug;
      await this.fetchArtist(artistSlug);
      // 밴드인 경우 멤버 정보도 다시 로드
      if (
        this.artist &&
        this.isArtistBand(this.artist.type) &&
        this.artist.members
      ) {
        await this.fetchMembersInfo();
      }

      // 개인 아티스트인 경우 소속 밴드 정보 다시 로드
      if (this.artist && !this.isArtistBand(this.artist.type)) {
        await this.fetchAffiliatedBands();
        // 소속 밴드 정보가 변경되면 공연 횟수도 다시 계산
        await this.calculateLiveCount();
      }
    },

    async handleArtistUpdated() {
      // ID 기반이므로 URL 변경 없이 데이터만 다시 로드
      // closeUpdatePopup에서 이미 처리됨
    },

    async fetchMembersInfo() {
      if (
        !this.artist ||
        !this.artist.members ||
        this.artist.members.length === 0
      ) {
        this.membersInfo = [];
        return;
      }

      try {
        this.membersInfo = [];

        // 각 멤버 ID로 문서를 직접 조회
        for (const memberId of this.artist.members) {
          try {
            const memberDocRef = doc(firestore, "artists", memberId);
            const memberDocSnap = await getDoc(memberDocRef);

            if (memberDocSnap.exists()) {
              const memberData = memberDocSnap.data();

              // 승인된 멤버만 표시
              if (memberData.approved) {
                let profileImageUrl = null;

                // 프로필 이미지 URL 생성
                if (memberData.userProfileImg) {
                  try {
                    const imageRef = storageRef(
                      storage,
                      `profile/${memberData.userProfileImg}`
                    );
                    profileImageUrl = await getDownloadURL(imageRef);
                  } catch (error) {
                    console.warn("멤버 프로필 이미지 로드 실패:", error);
                  }
                } else if (memberData.img) {
                  try {
                    const imageRef = storageRef(
                      storage,
                      `artist/${memberData.img}`
                    );
                    profileImageUrl = await getDownloadURL(imageRef);
                  } catch (error) {
                    console.warn("멤버 아티스트 이미지 로드 실패:", error);
                  }
                }

                this.membersInfo.push({
                  id: memberDocSnap.id, // 문서 ID
                  slug: memberData.slug ?? null,
                  name: memberData.name || "알 수 없음",
                  type: memberData.type || null,
                  profileImage: profileImageUrl,
                  detailLink: memberData.detailLink || null,
                });
              } else {
                // 승인되지 않은 멤버
                this.membersInfo.push({
                  id: memberId,
                  slug: memberData.slug ?? null,
                  name: memberData.name || "승인 대기",
                  type: null,
                  profileImage: null,
                  detailLink: null,
                });
              }
            } else {
              // 문서가 존재하지 않는 경우
              this.membersInfo.push({
                id: memberId,
                slug: null,
                name: "알 수 없음",
                type: null,
                profileImage: null,
                detailLink: null,
              });
            }
          } catch (error) {
            console.error(`멤버 ${memberId} 조회 오류:`, error);
            // 에러 발생 시에도 표시
            this.membersInfo.push({
              id: memberId,
              slug: null,
              name: "조회 실패",
              type: null,
              profileImage: null,
              detailLink: null,
            });
          }
        }
      } catch (error) {
        console.error("멤버 정보 조회 오류:", error);
        this.membersInfo = [];
      }
    },

    async fetchAffiliatedBands() {
      if (!this.artist || !this.artist.id) {
        this.affiliatedBands = [];
        return;
      }

      try {
        this.affiliatedBands = [];

        // myBands 필드에서 소속 밴드 ID 목록 가져오기
        const myBands = this.artist.myBands;
        if (!myBands || !Array.isArray(myBands) || myBands.length === 0) {
          this.affiliatedBands = [];
          return;
        }

        // 각 밴드 ID로 직접 문서 조회 (전체 컬렉션 스캔 불필요)
        for (const bandId of myBands) {
          try {
            const bandDocRef = doc(firestore, "artists", bandId);
            const bandDocSnap = await getDoc(bandDocRef);

            if (!bandDocSnap.exists()) {
              console.warn(`밴드 문서 없음: ${bandId}`);
              continue;
            }

            const bandData = bandDocSnap.data();

            // 승인된 밴드만 표시
            if (!bandData.approved) {
              continue;
            }

            // 밴드 타입인지 확인
            if (this.isArtistBand(bandData.type)) {
              let profileImageUrl = null;

              // 프로필 이미지 URL 생성
              if (bandData.userProfileImg) {
                try {
                  const imageRef = storageRef(
                    storage,
                    `profile/${bandData.userProfileImg}`
                  );
                  profileImageUrl = await getDownloadURL(imageRef);
                } catch (error) {
                  console.warn("밴드 프로필 이미지 로드 실패:", error);
                }
              } else if (bandData.img) {
                try {
                  const imageRef = storageRef(
                    storage,
                    `artist/${bandData.img}`
                  );
                  profileImageUrl = await getDownloadURL(imageRef);
                } catch (error) {
                  console.warn("밴드 아티스트 이미지 로드 실패:", error);
                }
              }

              // 해당 밴드의 공연 횟수 계산
              const bandLiveCount = await this.calculateBandLiveCount(
                bandData.name
              );

              this.affiliatedBands.push({
                id: bandDocSnap.id, // 문서 ID
                slug: bandData.slug ?? null,
                name: bandData.name,
                type: bandData.type,
                profileImage: profileImageUrl,
                detailLink: bandData.detailLink || null,
                description: bandData.description || null,
                members: bandData.members || [],
                liveCount: bandLiveCount,
              });
            }
          } catch (error) {
            console.error(`밴드 ${bandId} 조회 실패:`, error);
          }
        }
      } catch (error) {
        console.error("소속 밴드 정보 조회 오류:", error);
        this.affiliatedBands = [];
      }
    },

    async calculateLiveCount() {
      if (!this.artist || !this.artist.name) {
        return;
      }

      if (
        this.lastPerformanceArtistName === this.artist.name &&
        Array.isArray(this.performances)
      ) {
        this.artist.liveCount = this.performances.length;
        return;
      }

      try {
        const livesRef = collection(firestore, "lives");
        const approvedLivesQuery = query(livesRef, where("approved", "==", true));
        const livesSnapshot = await getDocs(approvedLivesQuery);
        let liveCount = 0;
        const artistName = this.artist.name;

        // 소속 밴드명 목록 (현재 아티스트가 밴드가 아닌 경우)
        const affiliatedBandNames = this.affiliatedBands.map(
          (band) => band.name
        );

        livesSnapshot.docs.forEach((doc) => {
          const liveData = doc.data();

          const lineup = liveData.lineup || [];
          const crew = Array.isArray(liveData.crew) ? liveData.crew : [];
          let isInLineup = false;
          let isInCrew = false;

          // 개별 아티스트로 참여한 공연 확인
          if (Array.isArray(lineup)) {
            isInLineup = lineup.includes(artistName);
          } else if (typeof lineup === "string") {
            isInLineup = lineup === artistName;
          }

          // 소속 밴드로 참여한 공연 확인 (밴드가 아닌 경우에만)
          if (
            !isInLineup &&
            this.artist.type !== "band" &&
            affiliatedBandNames.length > 0
          ) {
            if (Array.isArray(lineup)) {
              isInLineup = lineup.some((name) =>
                affiliatedBandNames.includes(name)
              );
            } else if (typeof lineup === "string") {
              isInLineup = affiliatedBandNames.includes(lineup);
            }
          }

          if (Array.isArray(crew) && crew.length > 0) {
            isInCrew = crew.some((member) => {
              if (typeof member === "object" && member !== null) {
                return (
                  String(member.name || "").trim() === artistName &&
                  String(member.role || "").trim()
                );
              }
              return false;
            });
          }

          if (isInLineup || isInCrew) {
            liveCount++;
          }
        });

        // 아티스트 객체의 liveCount 업데이트
        this.artist.liveCount = liveCount;
      } catch (error) {
        console.error("공연 횟수 계산 오류:", error);
        this.artist.liveCount = 0;
      }
    },

    async calculateBandLiveCount(bandName) {
      try {
        const livesSnapshot = await getDocs(collection(firestore, "lives"));
        let liveCount = 0;

        livesSnapshot.docs.forEach((doc) => {
          const liveData = doc.data();

          // 승인된 공연만 포함
          if (!liveData.approved) {
            return;
          }

          const lineup = liveData.lineup || [];

          // 해당 밴드가 라인업에 포함되어 있는지 확인
          if (Array.isArray(lineup)) {
            if (lineup.includes(bandName)) {
              liveCount++;
            }
          } else if (typeof lineup === "string") {
            if (lineup === bandName) {
              liveCount++;
            }
          }
        });

        return liveCount;
      } catch (error) {
        console.error("밴드 공연 횟수 계산 오류:", error);
        return 0;
      }
    },

    getMemberProfileImage(memberName) {
      const member = this.membersInfo.find((m) => m.name === memberName);
      return member ? member.profileImage : null;
    },

    getMemberType(memberName) {
      const member = this.membersInfo.find((m) => m.name === memberName);
      return member ? member.type : "other";
    },

    goToMember(memberName) {
      // 멤버가 등록된 아티스트인지 확인
      const member = this.membersInfo.find((m) => m.name === memberName);
      if (member && member.id && member.type) {
        // 등록된 아티스트면 아티스트 상세 페이지로 이동 (slug 기반)
        const artistRouteKey = member.slug ?? member.id;
        this.$router.push(`/artist/${artistRouteKey}`);
      } else if (member && member.detailLink) {
        // 상세 링크가 있으면 외부 링크로 이동
        window.open(member.detailLink, "_blank");
      } else {
        // 그 외의 경우 알림 표시
        this.$alert(`${memberName}의 상세 정보가 아직 등록되지 않았습니다.`);
      }
    },
    // 멤버 아티스트 찜 여부 확인
    isArtistLiked(artistId) {
      return this.userArtistLikes.includes(artistId);
    },
    // 멤버 아티스트 찜 토글
    async toggleArtistLike(artistId) {
      if (!this.userStore.isLogin) {
        this.userStore.showLoginPopup('artistLike');
        return;
      }

      const isCurrentlyLiked = this.isArtistLiked(artistId);

      try {
        const newLikedState = await toggleArtistLike(
          artistId,
          this.userStore.email,
          isCurrentlyLiked
        );

        // 로컬 상태 업데이트
        if (newLikedState) {
          // 찜 추가
          if (!this.userArtistLikes.includes(artistId)) {
            this.userArtistLikes.push(artistId);
          }
        } else {
          // 찜 해제
          this.userArtistLikes = this.userArtistLikes.filter(
            (id) => id !== artistId
          );
        }
      } catch (error) {
        console.error("아티스트 찜 오류:", error);
        this.$alert("오류가 발생했습니다. 다시 시도해주세요.");
      }
    },
    goToBand(bandName) {
      // 소속 밴드 페이지로 이동 (slug 기반)
      const band = this.affiliatedBands.find((b) => b.name === bandName);
      if (band && band.id) {
        const artistRouteKey = band.slug ?? band.id;
        this.$router.push(`/artist/${artistRouteKey}`);
      }
    },
    async loadUserDetailLink() {
      if (!this.userStore.isLogin) {
        this.userDetailLink = null;
        this.hasMyArtistCard = false;
        return;
      }

      try {
        this.userDetailLink = await getUserDetailLink(this.userStore.email);

        // 사용자의 트위터 링크가 있으면 해당 링크를 가진 아티스트 카드가 있는지 확인
        if (this.userDetailLink) {
          const { collection, query, where, getDocs } = await import('firebase/firestore');
          const { firestore } = await import('/firebaseConfig');
          const artistsRef = collection(firestore, "artists");
          const q = query(
            artistsRef,
            where("detailLink", "==", this.userDetailLink)
          );
          const querySnapshot = await getDocs(q);
          this.hasMyArtistCard = !querySnapshot.empty;
        } else {
          this.hasMyArtistCard = false;
        }
      } catch (error) {
        console.error("사용자 트위터 링크 로드 오류:", error);
        this.userDetailLink = null;
        this.hasMyArtistCard = false;
      }
    },

    async fetchAdBanners() {
      try {
        // banners 컬렉션에서 active가 true이고 type이 "detail"인 데이터 가져오기
        const bannersQuery = query(
          collection(firestore, "banners"),
          where("active", "==", true),
          where("type", "==", "detail")
        );
        const bannersSnapshot = await getDocs(bannersQuery);

        const customBannerData = await Promise.all(
          bannersSnapshot.docs.map(async (doc) => {
            const data = doc.data();

            // 광고 기간 체크 (startDateTime과 endDateTime이 있는 경우)
            if (data.startDateTime || data.endDateTime) {
              const now = new Date();

              // 시작일시 체크
              if (data.startDateTime) {
                const startDate = new Date(data.startDateTime);
                if (now < startDate) {
                  // 아직 광고 시작 전
                  return null;
                }
              }

              // 종료일시 체크
              if (data.endDateTime) {
                const endDate = new Date(data.endDateTime);
                if (now > endDate) {
                  // 광고 기간 종료됨
                  return null;
                }
              }
            }

            // 배너 이미지 URL 처리
            let bannerImgUrl = data.img;
            if (!bannerImgUrl) {
              console.error("배너 이미지가 없습니다:", data.title);
              return null;
            }

            if (!bannerImgUrl.startsWith("http")) {
              try {
                const imageRef = storageRef(storage, `banner/${data.img}`);
                bannerImgUrl = await getDownloadURL(imageRef);
              } catch (e) {
                console.error("배너 이미지 로드 실패:", e);
                return null;
              }
            }

            return {
              id: doc.id,
              source: "custom", // 출처 표시
              title: data.title,
              img: bannerImgUrl,
              link: data.link || "",
              date: data.date || "",
              place: data.place || "",
              type: "site", // 커스텀 배너는 site 타입으로
              priority: data.priority || 0, // 우선순위
            };
          })
        );

        const allBanners = customBannerData.filter(Boolean);

        // 우선순위로 정렬 (priority가 높을수록 먼저 표시)
        allBanners.sort((a, b) => {
          const priorityA = a.priority || 0;
          const priorityB = b.priority || 0;
          return priorityB - priorityA;
        });

        this.adBanners = allBanners;
        // console.log("배너 데이터 개수:", this.adBanners.length);
      } catch (error) {
        console.error("광고 배너 가져오기 실패:", error);
        this.adBanners = [];
      }
    },

    async goToAdLink(ad) {
      // 클릭 횟수 증가
      await this.incrementAdClickCount(ad);

      // 커스텀 배너(source: "custom")이고 링크가 있는 경우 외부 링크로 이동
      if (ad.source === "custom" && ad.link) {
        window.open(ad.link, "_blank");
      } else if (ad.source === "live") {
        // 라이브 배너인 경우 해당 라이브 상세 페이지로 이동
        this.$router.push({
          name: "LiveDetail",
          params: { slug: ad.slug || ad.id },
        });
      }
    },

    async incrementAdClickCount(ad) {
      try {
        await incrementBannerClickCount(ad);
      } catch (error) {
        console.error("광고 클릭 횟수 증가 오류:", error);
        // 클릭 횟수 증가 실패해도 사용자 경험에는 영향을 주지 않음
      }
    },

    async requestEditPermission() {
      if (!this.artist) return;

      const confirmed = await this.$confirm(
        `"${this.artist.name}" 아티스트 카드의 수정 권한을 신청하시겠습니까?\n\n권한신청 게시판으로 이동하여 신청 글을 작성해주세요.`
      );

      if (!confirmed) return;

      // 글쓰기 페이지로 이동 (권한신청 카테고리 자동 선택)
      this.$router.push({
        path: "/board/write",
        query: {
          category: "permission",
          artistName: this.artist.name,
        },
      });
    },

  },
};
</script>

<style scoped>
/* 로딩 상태 */
.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  min-height: 50vh;
}

.spinner {
  width: 100px;
  height: 100px;
  border: 10px solid #f3f3f3;
  border-top: 10px solid #66bdff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.loading p {
  font-size: 16px;
  color: #666;
  font-family: "NanumSquareRoundR", sans-serif;
}

.artist-detail-wrapper {
  width: 100%;
}

.detail-container {
  width: 90%;
  margin: auto;
  background: #fff;
  padding: 0px 16px;
  margin-bottom: 40px;
}

.detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 16px 0px;
}

.back-arrow-btn {
  background: transparent;
  border: none;
  font-size: 20px;
  cursor: pointer;
  padding: 6px 10px;
  color: #333;
  transition: opacity 0.2s ease;
}

.back-arrow-btn:hover {
  opacity: 0.7;
}

.share-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease;
}

.share-btn:hover {
  opacity: 0.7;
}

.share-icon {
  width: 22px;
  height: 22px;
  object-fit: contain;
}

.manage-btn-wrapper {
  position: relative;
  flex-shrink: 0;
}

.manage-settings-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease;
  border-radius: 6px;
}

.manage-settings-btn:hover {
  opacity: 0.7;
}

.settings-icon {
  width: 22px;
  height: 22px;
  object-fit: contain;
  opacity: 0.85;
}

.manage-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  min-width: 120px;
  z-index: 100;
  overflow: hidden;
}

.manage-dropdown-item {
  display: block;
  width: 100%;
  padding: 10px 16px;
  background: transparent;
  border: none;
  text-align: left;
  font-size: 0.9rem;
  color: #333;
  cursor: pointer;
  transition: background 0.15s;
}

.manage-dropdown-item:hover {
  background: #f5f5f5;
}

.manage-dropdown-item--danger {
  color: #e53935;
}

.manage-dropdown-item--danger:hover {
  background: #fff5f5;
}

.detail-title {
  width: 100%;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.title-left {
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
  flex: 1;
}

.detail-title h1 {
  font-size: 1.25rem;
  color: #333;
  margin: 0;
  word-break: keep-all;
  font-family: "Paperlogy8", "NanumSquareRoundEB", sans-serif;
  letter-spacing: -0.5px;
  line-height: 1.4;
}

.detail-more-link {
  font-size: 12px;
  color: #2196f3;
  cursor: pointer;
  text-decoration: none;
  font-family: "NanumSquareRoundR", sans-serif;
  white-space: nowrap;
}

.detail-more-link:hover {
  text-decoration: underline;
}

.mobile-only {
  display: flex;
}

.desktop-only {
  display: none;
}

.my-artist-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  background: linear-gradient(135deg,
      #ffd700,
      #ffed4e,
      #ff1493,
      #ff69b4,
      #00d4ff,
      #1e90ff,
      #ffd700);
  color: white;
  font-size: 0.75rem;
  font-weight: bold;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(255, 215, 0, 0.4);
  width: 100%;
  animation: badgeShimmer 3s ease-in-out infinite;
  background-size: 200% 200%;
  margin-top: 10px;
}

@keyframes badgeShimmer {
  0% {
    background-position: 0% 50%;
    box-shadow: 0 2px 8px rgba(255, 215, 0, 0.4);
  }

  50% {
    background-position: 100% 50%;
    box-shadow: 0 4px 12px rgba(255, 215, 0, 0.6);
  }

  100% {
    background-position: 0% 50%;
    box-shadow: 0 2px 8px rgba(255, 215, 0, 0.4);
  }
}

.artist-description {
  /* font-style: italic; */
  color: #666;
  font-weight: 400;
}

/* SNS 링크 스타일 */
.sns-links {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.sns-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 12px;
  text-decoration: none;
  font-size: 11px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.sns-link .sns-icon {
  width: 14px;
  height: 14px;
  object-fit: contain;
}

.youtube-link {
  background: rgba(255, 0, 0, 0.1);
  color: #ff0000;
  border: 1px solid rgba(255, 0, 0, 0.2);
}

.youtube-link:hover {
  background: rgba(255, 0, 0, 0.2);
  transform: translateY(-1px);
}

.instagram-link {
  background: linear-gradient(45deg, rgba(240, 148, 51, 0.1), rgba(225, 48, 108, 0.1));
  color: #e1306c;
  border: 1px solid rgba(225, 48, 108, 0.2);
}

.instagram-link:hover {
  background: linear-gradient(45deg, rgba(240, 148, 51, 0.2), rgba(225, 48, 108, 0.2));
  transform: translateY(-1px);
}

.twitter-link {
  background: #000;
  color: #fff;
  border: 1px solid #000;
}

.twitter-link img {
  height: 12px !important;
  width: 12px !important;
  padding: 1px !important;
  object-fit: contain;
}

.twitter-link:hover {
  background: #333;
  transform: translateY(-1px);
}

/* 틱톡 컨셉 뱃지: 다크 배경 + 시안·핑크 포인트 */
.tiktok-link {
  background: linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 50%, #0d0d0d 100%);
  color: #fff;
  border: 1px solid rgba(37, 244, 238, 0.35);
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(254, 44, 85, 0.08);
}

.tiktok-link .sns-icon {
  filter: drop-shadow(0 0 2px rgba(37, 244, 238, 0.5));
}

.tiktok-link:hover {
  background: linear-gradient(135deg, #1a1a1a 0%, #252525 50%, #141414 100%);
  border-color: rgba(37, 244, 238, 0.55);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.3), 0 0 12px rgba(37, 244, 238, 0.15);
  transform: translateY(-1px);
}

.soundcloud-link {
  background: rgba(255, 85, 0, 0.1);
  color: #ff5500;
  border: 1px solid rgba(255, 85, 0, 0.2);
}

.soundcloud-link:hover {
  background: rgba(255, 85, 0, 0.2);
  transform: translateY(-1px);
}

.other-link {
  background: rgba(102, 189, 255, 0.1);
  color: #66bdff;
  border: 1px solid rgba(102, 189, 255, 0.2);
}

.other-link:hover {
  background: rgba(102, 189, 255, 0.2);
  transform: translateY(-1px);
}

.delete-btn {
  background: #ff4d4d;
  color: #fff;
  border: none;
  border-radius: 5px;
  padding: 5px 10px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: "NanumSquareRoundR", sans-serif;
  transition: background 0.2s ease;
}

.delete-btn:hover {
  background: #ff3333;
}

.delete-btn:active {
  background: #cc0000;
}

.detail-content {
  display: flex;
  flex-direction: column;
  gap: 18px;
  margin-top: 10px;
  align-items: stretch;
}

.detail-poster-container {
  width: 100%;
  height: 350px;
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  background: #000;
}

.poster-blur-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-size: cover;
  background-position: center;
  filter: blur(4px);
  transform: scale(1.1);
  opacity: 0.3;
  z-index: 1;
}

.detail-poster {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  max-width: 90%;
  max-height: 90%;
  width: auto;
  height: auto;
  border-radius: 0px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  z-index: 2;
}

.no-image-placeholder {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  max-width: 90%;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 50%;
  padding: 30px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  max-height: 90%;
  width: auto;
  height: auto;
  z-index: 2;
}

.type-icon-large {
  width: 100px;
  height: 100px;
  object-fit: contain;
  opacity: 0.6;
}

.detail-info {
  gap: 16px;
  min-width: 0;
  width: 100%;
  padding: 0;
  margin-top: 0px;
  display: flex;
  flex-direction: column;
  margin-bottom: 24px;
}

.info-table {
  min-width: 0;
  width: 100%;
  gap: 12px;
  display: flex;
  flex-direction: column;
}

.info-row {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 16px;
}

.info-label {
  width: 70px;
  min-width: 70px;
  color: #888;
  font-size: 12px;
  font-weight: 600;
  text-align: left;
  font-family: "NanumSquareRoundEB", sans-serif;
  letter-spacing: -0.3px;
  margin-top: 4px;
}

.info-value {
  color: #333;
  font-size: 14px;
  font-weight: 400;
  text-align: left;
  word-break: keep-all;
  font-family: "Pretendard", sans-serif;
  line-height: 1.5;
  letter-spacing: -0.2px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.info-value p {
  margin: 0px;
}

.type-with-icon {
  display: flex;
  align-items: center;
  gap: 8px;
}

.type-icon {
  width: 22px;
  height: 22px;
  object-fit: contain;
  border: 1px solid #e0e0e0;
  border-radius: 50%;
  padding: 2px;
}

.detail-link-btn {
  background: #66bdff;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
}

.detail-link-btn:hover {
  background: #4a9eff;
}

/* 탭 섹션 스타일 */
.tabs-section {
  margin-top: 30px;
  width: 100%;
  background: #fafbfc;
  overflow: hidden;
  border-radius: 12px;
}

.tab-navigation {
  display: flex;
  background: #fff;
  border-bottom: 1px solid #e0e0e0;
}

.tab-button {
  flex: 1;
  padding: 14px 12px;
  border: none;
  background: none;
  font-size: 15px;
  font-weight: 500;
  color: #666;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: "NanumSquareRoundR", sans-serif;
  border-bottom: 3px solid transparent;
  border-radius: 0px;
}

.tab-button:hover {
  color: #333;
  background: #f5f5f5;
}

.tab-button.active {
  color: #2196f3;
  background: #f8fafe;
  border-bottom-color: #2196f3;
}

.tab-content {
  padding: 24px 16px 20px 16px;
  min-height: 200px;
}

.tab-panel {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.performances-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 20px;
  max-width: 1000px;
  margin: 0 auto;
}

/* 편집 권장 배너 (수정 권한 신청 배너와 동일 톤) */
.performance-recommend-banner {
  position: relative;
  padding: 12px 16px;
  margin: 15px auto 0;
  max-width: 1000px;
  background: linear-gradient(135deg, #f0e6ff 0%, #ffffff 100%);
  border-radius: 10px;
  border: 2px dashed #667eea;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: claimBannerPulse 2s ease-in-out infinite;
  cursor: pointer;
  transition: all 0.3s ease;
}

.performance-recommend-banner:hover {
  transform: translateY(-2px);
  border-style: solid;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.4);
  background: linear-gradient(135deg, #e6d9ff 0%, #f9f7ff 100%);
}

.performance-recommend-banner .claim-banner-content {
  margin: 0 auto;
}

/* 작품활동(비디오) 스타일 */
.videos-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 1000px;
  margin: 0 auto;
}

/* 플로팅 버튼 */
.floating-button {
  position: fixed;
  right: calc(50% - 500px);
  max-width: 1024px;
  margin-right: 12px;
  height: 46px;
  padding: 0px 12px;
  border-radius: 30px;
  background: #66bdff;
  color: white;
  border: none;
  box-shadow: 0 4px 16px rgba(102, 189, 255, 0.4);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 199;
  font-size: 1rem;
  font-weight: bold;
  font-family: "NanumSquareRoundB", sans-serif !important;
  transition: all 0.3s ease;
  bottom: 20px !important;
}

@media screen and (max-width: 1024px) {
  .floating-button {
    right: 12px;
  }
}

.floating-button:hover {
  background: #4a9eff;
  transform: scale(1.1);
  box-shadow: 0 6px 20px rgba(102, 189, 255, 0.6);
}

.floating-button:active {
  transform: scale(0.95);
}

.artist-info-content {
  margin: 0 auto;
}

.info-item {
  margin-bottom: 20px;
  padding: 18px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.info-item-label {
  font-size: 15px;
  font-weight: bold;
  color: #333;
  margin-bottom: 10px;
  font-family: "NanumSquareRoundB", sans-serif;
}

.info-item-value {
  font-size: 15px;
  color: #666;
  margin: 0;
  font-family: "NanumSquareRoundR", sans-serif;
}

.empty-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #999;
  font-size: 15px;
  padding: 50px 20px;
  font-family: "NanumSquareRoundR", sans-serif;
}

.empty-content .spinner {
  width: 60px;
  height: 60px;
  border: 6px solid #f3f3f3;
  border-top: 6px solid #66bdff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

.empty-content p {
  margin: 0;
  color: #999;
  font-size: 15px;
  font-family: "NanumSquareRoundR", sans-serif;
}

.likeBtnOverlay {
  position: absolute;
  bottom: 12px;
  right: 12px;
  color: black;
  font-weight: bold;
  font-family: "NanumSquareRoundR", sans-serif;
  padding: 0px 5px;
  border-radius: 5px;
  padding-bottom: 2px;
  z-index: 3;
}

.like-heart {
  padding: 5px;
  margin: 0px 2px;
  border-radius: 15px;
  height: 30px !important;
  width: 30px !important;
  border: 1px solid #ccc;
}

.like-heart.liked {
  background-color: white;
}

.edit-btn {
  background: #4a9eff;
  color: white;
  border: none;
  border-radius: 5px;
  padding: 5px 10px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: "NanumSquareRoundR", sans-serif;
  transition: background 0.2s ease;
}

.edit-btn:hover {
  background: #2196f3;
}

.edit-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}

/* info-row 안의 삭제 버튼 스타일 */
.info-row .delete-btn {
  background: #ff4d4d;
  color: white;
  border: none;
  border-radius: 5px;
  padding: 5px 10px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: "NanumSquareRoundR", sans-serif;
  transition: background 0.2s ease;
}

.info-row .delete-btn:hover {
  background: #ff3333;
}

.info-row .delete-btn:active {
  background: #cc0000;
}

/* 수정 권한 신청 배너 스타일 */
.claim-banner {
  position: relative;
  padding: 12px 16px;
  margin: 12px;
  background: linear-gradient(135deg, #f0e6ff 0%, #ffffff 100%);
  border-radius: 10px;
  border: 2px dashed #667eea;
  cursor: pointer;
  transition: all 0.3s ease;
  overflow: hidden;
  margin: 12px auto;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: claimBannerPulse 2s ease-in-out infinite;
}

.claim-banner:hover {
  transform: translateY(-2px);
  border-style: solid;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.4);
  background: linear-gradient(135deg, #e6d9ff 0%, #f9f7ff 100%);
}

.claim-banner-glow {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 50% 50%,
      rgba(102, 126, 234, 0.1) 0%,
      transparent 70%);
  pointer-events: none;
}

.claim-banner-content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: row;
  text-align: left;
  align-items: center;
  gap: 12px;
  text-align: left;
}

.claim-icon-circle {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
  animation: claimIconBounce 2s ease-in-out infinite;
  flex-shrink: 0;
}

.claim-icon {
  font-size: 21px;
  animation: claimIconRotate 3s ease-in-out infinite;
}

.claim-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.claim-title {
  margin: 0;
  font-size: 0.85rem;
  font-weight: bold;
  color: #333;
  font-family: "NanumSquareRoundB", sans-serif;
}

.claim-subtitle {
  margin: 0;
  font-size: 0.7rem;
  color: #666;
  font-family: "NanumSquareRoundR", sans-serif;
  line-height: 1.3;
}

.claim-badge {
  display: inline-block;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 2px 8px;
  border-radius: 8px;
  font-size: 0.65rem;
  font-weight: bold;
  font-family: "NanumSquareRoundB", sans-serif;
  box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
  margin-top: 2px;
  width: fit-content;
}

@keyframes claimBannerPulse {

  0%,
  100% {
    box-shadow: 0 2px 10px rgba(102, 126, 234, 0.2);
  }

  50% {
    box-shadow: 0 4px 20px rgba(102, 126, 234, 0.4);
  }
}

@keyframes claimIconBounce {

  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-5px);
  }
}

@keyframes claimIconRotate {

  0%,
  100% {
    transform: rotate(0deg) scale(1);
  }

  25% {
    transform: rotate(-10deg) scale(1.1);
  }

  50% {
    transform: rotate(0deg) scale(1);
  }

  75% {
    transform: rotate(10deg) scale(1.1);
  }
}

/* 관리자 전용 섹션 스타일 */
.admin-section {
  width: 100%;
  border-radius: 12px;
  background: linear-gradient(135deg, #f8f9fa 0%, #fff 100%);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  margin-top: 20px;
}

.admin-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease;
}

.admin-section-header:hover {
  background: rgba(0, 0, 0, 0.02);
}

.admin-section-title {
  font-size: 12px;
  font-weight: 500;
  color: #888;
  font-family: "NanumSquareRoundR", sans-serif;
  letter-spacing: -0.3px;
}

.admin-section-toggle {
  font-size: 10px;
  color: #bbb;
  transition: transform 0.3s ease;
}

.admin-section-content {
  padding: 16px 16px;
}

.admin-info-table {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-bottom: 20px;
}

.admin-info-row {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.admin-info-label {
  width: 65px;
  min-width: 65px;
  font-size: 10px;
  font-weight: 500;
  color: #aaa;
  font-family: "NanumSquareRoundR", sans-serif;
}

.admin-info-value {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 12px;
  color: #555;
  font-family: "NanumSquareRoundR", sans-serif;
}

.admin-sub-text {
  font-size: 11px;
  color: #999;
}

.admin-date-text {
  font-size: 11px;
  color: #bbb;
}

.admin-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

/* 찜 정보 스타일 */
.like-count-badge {
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(255, 107, 157, 0.15) 0%, rgba(255, 107, 157, 0.08) 100%);
  color: #e91e63;
  font-weight: 500;
  font-family: "NanumSquareRoundB", sans-serif;
}

.show-liked-btn {
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 12px;
  border: 1px solid #ddd;
  background: white;
  color: #666;
  cursor: pointer;
  font-family: "NanumSquareRoundR", sans-serif;
  transition: all 0.2s ease;
}

.show-liked-btn:hover {
  border-color: #e91e63;
  color: #e91e63;
  background: rgba(255, 107, 157, 0.05);
}

.show-liked-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.liked-users-row {
  align-items: flex-start !important;
}

.liked-users-list {
  flex-direction: column !important;
  align-items: flex-start !important;
  gap: 6px !important;
  max-height: 200px;
  overflow-y: auto;
  padding: 8px;
  background: rgba(0, 0, 0, 0.02);
  border-radius: 8px;
  width: 100%;
}

.liked-user-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  width: 100%;
}

.liked-user-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid rgba(0, 0, 0, 0.1);
}

.liked-user-avatar-placeholder {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  border-radius: 50%;
  font-size: 14px;
}

.liked-user-name {
  font-size: 12px;
  color: #333;
  font-family: "NanumSquareRoundB", sans-serif;
}

.liked-user-email {
  font-size: 10px;
  color: #999;
}

/* 밴드 멤버 탭 스타일 */
.members-content {
  max-width: 800px;
  margin: 0 auto;
}

.members-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 18px;
}

.member-card {
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
}

.member-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.member-info {
  padding: 14px;
  text-align: center;
}

.member-avatar {
  margin-bottom: 12px;
}

.member-profile-image {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #f0f0f0;
}

.member-no-image {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  border: 2px solid #f0f0f0;
}

.member-type-icon {
  width: 28px;
  height: 28px;
  object-fit: contain;
  opacity: 0.5;
}

.member-details {
  text-align: center;
}

.member-name {
  font-size: 15px;
  text-align: center;
  font-weight: bold;
  color: #333;
  margin: 0 0 4px 0;
  font-family: "NanumSquareRoundB", sans-serif;
}

.member-type {
  font-size: 13px;
  color: #666;
  margin: 0;
  font-family: "NanumSquareRoundR", sans-serif;
}

/* 소속 밴드 탭 스타일 */
.bands-content {
  margin: 0 auto;
}

.affiliated-band-edit-banner,
.band-member-edit-banner {
  margin: 14px auto 0;
  cursor: pointer;
  animation: none;
}

.affiliated-band-edit-banner:hover,
.band-member-edit-banner:hover {
  transform: none;
  border-style: dashed;
  box-shadow: none;
  background: linear-gradient(135deg, #f0e6ff 0%, #ffffff 100%);
}

.bands-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 18px;
}

.band-card {
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
}

.band-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
}

.band-info {
  padding: 18px;
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.band-avatar {
  flex-shrink: 0;
}

.band-profile-image {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #f0f0f0;
}

.band-no-image {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #f0f0f0;
}

.band-type-icon {
  width: 35px;
  height: 35px;
  object-fit: contain;
  opacity: 0.5;
}

.band-details {
  flex: 1;
  min-width: 0;
}

.band-name {
  font-size: 17px;
  font-weight: bold;
  color: #333;
  margin: 0 0 6px 0;
  font-family: "NanumSquareRoundB", sans-serif;
}

.band-stats {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 8px;
}

.band-member-count {
  font-size: 13px;
  color: #666;
  margin: 0;
  font-family: "NanumSquareRoundR", sans-serif;
}

.band-live-count {
  font-size: 13px;
  color: #1976d2;
  margin: 0;
  font-family: "NanumSquareRoundR", sans-serif;
  font-weight: 500;
}

.band-description {
  font-size: 12px;
  color: #888;
  margin: 0;
  line-height: 1.4;
  font-family: "NanumSquareRoundR", sans-serif;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* ========================================
   반응형 스타일
   - Medium: 481px ~ (기본 스타일, 최대 1024px)
   - Small: ~480px (모바일 화면)
   ======================================== */

/* Medium 스타일은 기본 스타일로 이동됨 */

/* ========================================
   Small (~480px): 모바일 화면
   ======================================== */
@media screen and (max-width: 480px) {
  .spinner {
    width: 50px;
    height: 50px;
    border: 5px solid #f3f3f3;
    border-top: 5px solid #66bdff;
  }

  .loading p {
    font-size: 13px;
  }

  .detail-container {
    padding: 0px 10px;
  }

  .detail-title h1 {
    font-size: 1.1rem;
  }

  .detail-more-link {
    font-size: 11px;
  }

  .back-arrow-btn {
    font-size: 18px;
    padding: 4px 8px;
  }

  .share-icon {
    width: 20px;
    height: 20px;
  }

  .edit-btn,
  .delete-btn {
    font-size: 12px;
    padding: 5px 10px;
    border-radius: 5px;
  }

  .detail-content {
    gap: 16px;
  }

  .detail-poster-container {
    height: 280px;
    border-radius: 10px;
  }

  .detail-poster {
    max-width: 85%;
    max-height: 85%;
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.3);
  }

  .type-icon-large {
    width: 70px;
    height: 70px;
  }

  .detail-info {
    gap: 8px;
  }

  .info-table {
    gap: 6px;
  }

  .info-label {
    width: 55px;
    min-width: 55px;
    font-size: 11px;
  }

  .info-value {
    font-size: 12px;
  }

  .my-artist-badge {
    font-size: 0.65rem;
    padding: 3px 8px;
  }

  .sns-links {
    gap: 8px;
  }

  .sns-link {
    padding: 3px 6px;
    font-size: 10px;
    gap: 3px;
  }

  .sns-link .sns-icon {
    width: 12px;
    height: 12px;
  }

  .twitter-link img {
    height: 11px !important;
    width: 11px !important;
  }

  .tabs-section {
    margin-top: 20px;
  }

  .tab-button {
    padding: 10px 6px;
    font-size: 12px;
  }

  .tab-content {
    padding: 12px 6px 10px 6px;
  }

  .performances-grid {
    gap: 12px;
  }

  .videos-grid {
    gap: 10px;
  }

  .info-item {
    margin-bottom: 14px;
    padding: 14px;
  }

  .info-item-label {
    font-size: 12px;
    margin-bottom: 6px;
  }

  .info-item-value {
    font-size: 12px;
  }

  .empty-content {
    padding: 35px 12px;
    font-size: 13px;
  }

  .empty-content .spinner {
    width: 50px;
    height: 50px;
    border: 5px solid #f3f3f3;
    border-top: 5px solid #66bdff;
    margin-bottom: 15px;
  }

  .empty-content p {
    font-size: 13px;
  }

  /* 밴드 멤버 탭 Small */
  .members-grid {
    grid-template-columns: repeat(1, 1fr);
    gap: 12px;
  }

  .member-card {
    border-radius: 8px;
  }

  .member-info {
    padding: 10px;
  }

  .member-profile-image,
  .member-no-image {
    width: 50px;
    height: 50px;
  }

  .member-type-icon {
    width: 20px;
    height: 20px;
  }

  .member-name {
    font-size: 12px;
  }

  .member-type {
    font-size: 10px;
  }

  /* 소속 밴드 Small */
  .bands-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .band-card {
    border-radius: 8px;
  }

  .band-info {
    padding: 12px;
    gap: 10px;
  }

  .band-profile-image,
  .band-no-image {
    width: 50px;
    height: 50px;
  }

  .band-type-icon {
    width: 25px;
    height: 25px;
  }

  .band-name {
    font-size: 14px;
  }

  .band-stats {
    flex-direction: column;
  }

  .band-member-count,
  .band-live-count {
    font-size: 12px;
  }

  .band-description {
    font-size: 11px;
  }

  /* Small 관리자 섹션 */
  .admin-section {
    border-radius: 10px;
  }

  .admin-section-header {
    padding: 10px 14px;
  }

  .admin-section-title {
    font-size: 11px;
  }

  .admin-section-content {
    padding: 14px 14px;
  }

  .admin-info-label {
    width: 55px;
    min-width: 55px;
    font-size: 10px;
  }

  .admin-info-value {
    font-size: 11px;
  }

  .admin-sub-text {
    font-size: 10px;
  }

  .admin-date-text {
    font-size: 10px;
  }

  /* 수정 권한 신청 배너 / 편집 권장 배너 Small */
  .claim-banner,
  .performance-recommend-banner {
    padding: 8px 10px;
  }

  .claim-banner-content {
    gap: 8px;
  }

  .claim-icon-circle {
    width: 35px;
    height: 35px;
  }

  .claim-icon {
    font-size: 18px;
  }

  .claim-title {
    font-size: 0.75rem;
  }

  .claim-subtitle {
    font-size: 0.55rem;
  }

  .claim-badge {
    font-size: 0.55rem;
    padding: 1px 5px;
  }
}
</style>
